/**
 * Wizard multi-step: crear cliente (o reusar) + campaña + avisos + placements
 * en una sola sesión guiada. Pasos:
 *   1. Cliente
 *   2. Datos campaña
 *   3. Avisos
 *   4. Placements (uno por aviso × medio)
 *   5. Confirmación y creación en cascada
 */
import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, AlertCircle, Loader2, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { OutletPicker } from "@/components/feature/media/outlet-picker";
import { PeruGeoPicker, type GeoValue } from "@/components/feature/media/peru-geo-picker";
import { PriceEstimatePreview } from "@/components/feature/media/price-estimate-preview";
import {
  media,
  AD_TYPE_LABELS,
  type AdType,
  type Coverage,
  type ContentFormat,
  type AgencyClientOut,
} from "@/lib/api";

export const Route = createFileRoute("/_auth/media/campaigns_/new")({
  component: NewCampaignWizard,
});

type ClientChoice = "existing" | "new";

type NewClient = {
  name: string;
  code: string;
  ruc: string;
  contact_name: string;
  contact_email: string;
  phone: string;
  industry: string;
};

type CampaignData = {
  name: string;
  code: string;
  description: string;
  period_start: string;
  period_end: string;
  budget_amount: string;
};

type AdDraft = {
  _key: string;
  title: string;
  ad_type: AdType;
  summary: string;
};

type PlacementDraft = {
  _key: string;
  ad_index: number;
  outlet_id: number | null;
  outlet_accepts: string[];
  coverage: Coverage;
  geo: GeoValue;
  requested_at: string;
  scheduled_at: string;
  end_at: string;
  duration_days: string;
  repetitions: string;
  spot_duration_seconds: string;
  word_count: string;
  content_format: ContentFormat | "";
  cost_amount: string;
  price_amount: string;
};

const STEPS = ["Cliente", "Campaña", "Avisos", "Placements", "Confirmar"] as const;

/** UID con fallback para contextos http (no localhost, no https) donde
 *  crypto.randomUUID lanza TypeError. */
function uid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try { return crypto.randomUUID(); } catch { /* fallback */ }
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

type ValidationError = {
  step: number;
  stepLabel: string;
  field: string;
  message: string;
  context?: string;
};

function validateAll(state: {
  choice: ClientChoice; selectedClientId: number | null; newClient: NewClient;
  campaign: CampaignData; ads: AdDraft[]; placements: PlacementDraft[];
}): ValidationError[] {
  const errors: ValidationError[] = [];

  // Step 1 — Cliente
  if (state.choice === "existing" && !state.selectedClientId) {
    errors.push({ step: 0, stepLabel: "Cliente", field: "client_id", message: "Selecciona un cliente existente" });
  }
  if (state.choice === "new") {
    if (!state.newClient.name) errors.push({ step: 0, stepLabel: "Cliente", field: "name", message: "Nombre del cliente requerido" });
    if (!state.newClient.code) errors.push({ step: 0, stepLabel: "Cliente", field: "code", message: "Código del cliente requerido (prefijo ADV)" });
    if (state.newClient.code && !/^[A-Za-z0-9_-]+$/.test(state.newClient.code)) {
      errors.push({ step: 0, stepLabel: "Cliente", field: "code", message: "Código solo admite letras, números, guion y _" });
    }
  }

  // Step 2 — Campaña
  if (!state.campaign.name) errors.push({ step: 1, stepLabel: "Campaña", field: "name", message: "Nombre de la campaña requerido" });
  if (state.campaign.period_start && state.campaign.period_end && state.campaign.period_start > state.campaign.period_end) {
    errors.push({ step: 1, stepLabel: "Campaña", field: "period", message: "Inicio del período no puede ser mayor que el fin" });
  }

  // Step 3 — Avisos (OPCIONAL — la campaña puede crearse sin avisos)
  state.ads.forEach((a, i) => {
    const ctx = `Aviso #${i + 1}`;
    if (!a.title) errors.push({ step: 2, stepLabel: "Avisos", field: `ad.title`, message: "Título requerido", context: ctx });
    if (!a.ad_type) errors.push({ step: 2, stepLabel: "Avisos", field: `ad.type`, message: "Tipo de aviso requerido", context: ctx });
  });

  // Step 4 — Placements (OPCIONAL — se pueden agregar luego en el detalle del aviso)
  state.placements.forEach((p, i) => {
    const adRef = state.ads[p.ad_index];
    const ctx = `Placement #${i + 1}${adRef ? ` (aviso ${adRef.title || `#${p.ad_index + 1}`})` : ""}`;
    if (!p.outlet_id) errors.push({ step: 3, stepLabel: "Placements", field: "outlet_id", message: "Medio requerido", context: ctx });
    if (!p.coverage) errors.push({ step: 3, stepLabel: "Placements", field: "coverage", message: "Cobertura requerida", context: ctx });
    // Reglas geo cascada
    if (p.coverage === "regional" && !p.geo.region_id) {
      errors.push({ step: 3, stepLabel: "Placements", field: "region_id", message: "Cobertura regional requiere seleccionar una región", context: ctx });
    }
    if ((p.coverage === "local" || p.coverage === "zona")) {
      if (!p.geo.region_id) {
        errors.push({ step: 3, stepLabel: "Placements", field: "region_id", message: `Cobertura ${p.coverage} requiere región`, context: ctx });
      }
      if (!p.geo.province_id) {
        errors.push({ step: 3, stepLabel: "Placements", field: "province_id", message: `Cobertura ${p.coverage} requiere provincia`, context: ctx });
      }
    }
    if (p.coverage === "nacional" && (p.geo.region_id || p.geo.province_id || p.geo.district_id)) {
      errors.push({ step: 3, stepLabel: "Placements", field: "geo", message: "Cobertura nacional NO debe tener región/provincia/distrito", context: ctx });
    }
  });

  return errors;
}

function NewCampaignWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [showErrors, setShowErrors] = useState<ValidationError[] | null>(null);

  // Step 1
  const [choice, setChoice] = useState<ClientChoice>("existing");
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [newClient, setNewClient] = useState<NewClient>({
    name: "", code: "", ruc: "", contact_name: "", contact_email: "", phone: "", industry: "",
  });

  // Step 2
  const [campaign, setCampaign] = useState<CampaignData>({
    name: "", code: "", description: "", period_start: "", period_end: "", budget_amount: "",
  });

  // Step 3
  const [ads, setAds] = useState<AdDraft[]>([]);

  // Step 4
  const [placements, setPlacements] = useState<PlacementDraft[]>([]);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<string>("");

  const clientsQ = useQuery({
    queryKey: ["media", "clients"],
    queryFn: () => media.listClients({ limit: 200, is_active: true }),
  });

  // Errores del paso actual (para validación inmediata al hacer "Siguiente")
  function validateCurrentStep(): ValidationError[] {
    const all = validateAll({ choice, selectedClientId, newClient, campaign, ads, placements });
    return all.filter(e => e.step === step);
  }

  function tryAdvance() {
    const errs = validateCurrentStep();
    if (errs.length > 0) {
      setShowErrors(errs);
      // Toast resumen + abre modal
      toast.error(`${errs.length} ${errs.length === 1 ? "campo requerido" : "campos requeridos"} en este paso`);
      return;
    }
    setStep(s => s + 1);
  }

  function addAd() {
    setAds([...ads, {
      _key: uid(),
      title: "", ad_type: "aviso_corte", summary: "",
    }]);
  }

  function updateAd(idx: number, patch: Partial<AdDraft>) {
    setAds(ads.map((a, i) => i === idx ? { ...a, ...patch } : a));
  }

  function removeAd(idx: number) {
    setAds(ads.filter((_, i) => i !== idx));
    // También limpia placements asociados
    setPlacements(placements.filter(p => p.ad_index !== idx).map(p => ({
      ...p,
      ad_index: p.ad_index > idx ? p.ad_index - 1 : p.ad_index,
    })));
  }

  function addPlacement(adIndex: number) {
    setPlacements([...placements, {
      _key: uid(),
      ad_index: adIndex,
      outlet_id: null,
      outlet_accepts: [],
      coverage: "nacional",
      geo: { region_id: null, province_id: null, district_id: null },
      requested_at: "", scheduled_at: "", end_at: "",
      duration_days: "", repetitions: "1",
      spot_duration_seconds: "", word_count: "", content_format: "",
      cost_amount: "", price_amount: "",
    }]);
  }

  function updatePlacement(idx: number, patch: Partial<PlacementDraft>) {
    setPlacements(placements.map((p, i) => i === idx ? { ...p, ...patch } : p));
  }

  function removePlacement(idx: number) {
    setPlacements(placements.filter((_, i) => i !== idx));
  }

  async function executeCreate() {
    // Validación final completa antes de submit
    const allErrs = validateAll({ choice, selectedClientId, newClient, campaign, ads, placements });
    if (allErrs.length > 0) {
      setShowErrors(allErrs);
      // Auto-saltar al primer step con errores
      const firstStep = Math.min(...allErrs.map(e => e.step));
      setStep(firstStep);
      toast.error(`${allErrs.length} ${allErrs.length === 1 ? "campo requerido" : "campos requeridos"} antes de crear`);
      return;
    }
    setSubmitting(true);
    try {
      // 1. Cliente
      let clientId = selectedClientId!;
      if (choice === "new") {
        setProgress("Creando cliente...");
        const c = await media.createClient({
          name: newClient.name, code: newClient.code,
          ruc: newClient.ruc || undefined,
          contact_name: newClient.contact_name || undefined,
          contact_email: newClient.contact_email || undefined,
          phone: newClient.phone || undefined,
          industry: newClient.industry || undefined,
        });
        clientId = c.id;
      }

      // 2. Campaña
      setProgress("Creando campaña...");
      const camp = await media.createCampaign({
        client_id: clientId,
        name: campaign.name,
        code: campaign.code || undefined,
        description: campaign.description || undefined,
        period_start: campaign.period_start || undefined,
        period_end: campaign.period_end || undefined,
        budget_amount: campaign.budget_amount ? Number(campaign.budget_amount) : undefined,
      });

      // 3. Avisos
      const adIds: number[] = [];
      for (let i = 0; i < ads.length; i++) {
        setProgress(`Creando aviso ${i + 1}/${ads.length}...`);
        const a = await media.createAd({
          campaign_id: camp.id,
          title: ads[i].title,
          ad_type: ads[i].ad_type,
          summary: ads[i].summary || undefined,
        });
        adIds.push(a.id);
      }

      // 4. Placements
      for (let i = 0; i < placements.length; i++) {
        setProgress(`Creando placement ${i + 1}/${placements.length}...`);
        const p = placements[i];
        await media.createPlacement({
          ad_id: adIds[p.ad_index],
          outlet_id: p.outlet_id!,
          coverage: p.coverage,
          region_id: p.geo.region_id ?? undefined,
          province_id: p.geo.province_id ?? undefined,
          district_id: p.geo.district_id ?? undefined,
          requested_at: p.requested_at || undefined,
          scheduled_at: p.scheduled_at || undefined,
          end_at: p.end_at || undefined,
          duration_days: p.duration_days ? Number(p.duration_days) : undefined,
          repetitions: p.repetitions ? Number(p.repetitions) : 1,
          spot_duration_seconds: p.spot_duration_seconds ? Number(p.spot_duration_seconds) : undefined,
          word_count: p.word_count ? Number(p.word_count) : undefined,
          content_format: p.content_format || undefined,
          cost_amount: p.cost_amount ? Number(p.cost_amount) : undefined,
          price_amount: p.price_amount ? Number(p.price_amount) : undefined,
        });
      }

      toast.success(
        ads.length || placements.length
          ? `Campaña "${camp.name}" creada con ${ads.length} avisos y ${placements.length} placements`
          : `Campaña "${camp.name}" creada. Agrega avisos cuando quieras.`,
      );
      navigate({ to: "/media/campaigns/$campaignId", params: { campaignId: String(camp.id) } });
    } catch (e) {
      const msg = (e as Error).message || "Error desconocido";
      toast.error(`Error: ${msg}`);
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto">
      <Stepper current={step} />

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>{STEPS[step]}</CardTitle>
        </CardHeader>
        <CardContent>
          {step === 0 && (
            <ClientStep
              choice={choice} setChoice={setChoice}
              clients={clientsQ.data ?? []}
              isLoading={clientsQ.isLoading}
              selectedClientId={selectedClientId}
              setSelectedClientId={setSelectedClientId}
              newClient={newClient} setNewClient={setNewClient}
            />
          )}
          {step === 1 && (
            <CampaignStep campaign={campaign} setCampaign={setCampaign} />
          )}
          {step === 2 && (
            <AdsStep ads={ads} addAd={addAd} updateAd={updateAd} removeAd={removeAd} />
          )}
          {step === 3 && (
            <PlacementsStep
              ads={ads}
              placements={placements}
              addPlacement={addPlacement}
              updatePlacement={updatePlacement}
              removePlacement={removePlacement}
            />
          )}
          {step === 4 && (
            <ConfirmStep
              choice={choice}
              clientName={choice === "new" ? newClient.name : (clientsQ.data ?? []).find(c => c.id === selectedClientId)?.name ?? "?"}
              campaign={campaign}
              ads={ads}
              placements={placements}
              submitting={submitting}
              progress={progress}
            />
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between mt-6">
        <Button
          variant="outline"
          onClick={() => setStep(s => Math.max(0, s - 1))}
          disabled={step === 0 || submitting}
        >
          <ArrowLeft size={14} /> Anterior
        </Button>

        {step < STEPS.length - 1 ? (
          <div className="flex items-center gap-2">
            {step >= 1 && (
              <Button variant="outline" onClick={executeCreate} disabled={submitting}>
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Crear ahora
              </Button>
            )}
            <Button onClick={tryAdvance}>
              Siguiente <ArrowRight size={14} />
            </Button>
          </div>
        ) : (
          <Button onClick={executeCreate} disabled={submitting}>
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Crear campaña
          </Button>
        )}
      </div>

      {/* Modal de errores requeridos */}
      <ValidationErrorsDialog
        errors={showErrors}
        onClose={() => setShowErrors(null)}
        onJumpToStep={(s) => { setStep(s); setShowErrors(null); }}
      />
    </div>
  );
}

// ============ Modal de errores ============

function ValidationErrorsDialog({
  errors, onClose, onJumpToStep,
}: {
  errors: ValidationError[] | null;
  onClose: () => void;
  onJumpToStep: (step: number) => void;
}) {
  if (!errors) return null;
  // Agrupar por step
  const byStep: Record<number, ValidationError[]> = {};
  for (const e of errors) (byStep[e.step] ||= []).push(e);

  return (
    <Dialog open={!!errors} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle size={18} className="text-bad" />
            Faltan {errors.length} {errors.length === 1 ? "campo requerido" : "campos requeridos"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {Object.entries(byStep).sort(([a], [b]) => Number(a) - Number(b)).map(([stepIdx, errs]) => (
            <div key={stepIdx} className="border border-ink-200 dark:border-ink-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">
                  Paso {Number(stepIdx) + 1} — {errs[0].stepLabel}
                </div>
                <Button size="sm" variant="outline" onClick={() => onJumpToStep(Number(stepIdx))}>
                  Ir al paso <ArrowRight size={12} />
                </Button>
              </div>
              <ul className="space-y-1 text-xs text-ink-600 dark:text-ink-300">
                {errs.map((e, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-bad mt-0.5">•</span>
                    <div>
                      {e.context && <span className="font-mono text-[10px] bg-ink-100 dark:bg-ink-800 px-1 rounded mr-1">{e.context}</span>}
                      {e.message}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={onClose}><X size={14} /> Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================
// Stepper
// =========================================================

function Stepper({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2 flex-1">
          <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
            i < current ? "bg-emerald-500 text-white" :
            i === current ? "bg-brand-600 text-white" :
            "bg-ink-100 text-ink-500"
          }`}>
            {i < current ? <Check size={14} /> : i + 1}
          </div>
          <div className={`text-xs font-medium ${i <= current ? "text-ink-900 dark:text-white" : "text-ink-400"}`}>
            {s}
          </div>
          {i < STEPS.length - 1 && <div className="flex-1 h-px bg-ink-200" />}
        </div>
      ))}
    </div>
  );
}

// =========================================================
// Step 1 — Cliente
// =========================================================

function ClientStep(props: {
  choice: ClientChoice;
  setChoice: (c: ClientChoice) => void;
  clients: AgencyClientOut[];
  isLoading: boolean;
  selectedClientId: number | null;
  setSelectedClientId: (id: number | null) => void;
  newClient: NewClient;
  setNewClient: (c: NewClient) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant={props.choice === "existing" ? "default" : "outline"}
          onClick={() => props.setChoice("existing")}
        >
          Cliente existente
        </Button>
        <Button
          variant={props.choice === "new" ? "default" : "outline"}
          onClick={() => props.setChoice("new")}
        >
          Nuevo cliente
        </Button>
      </div>

      {props.choice === "existing" ? (
        <div>
          <Label className="text-xs">Cliente *</Label>
          {props.isLoading ? <Skeleton className="h-10" /> : (
            <Select
              value={props.selectedClientId ? String(props.selectedClientId) : ""}
              onValueChange={(v) => props.setSelectedClientId(Number(v))}
            >
              <SelectTrigger><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
              <SelectContent>
                {props.clients.map(c => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name} <span className="text-xs text-ink-400">({c.code})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!props.isLoading && !props.clients.length && (
            <p className="text-xs text-ink-500 mt-2 flex items-center gap-1">
              <AlertCircle size={12} /> No hay clientes. Crea uno nuevo.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Nombre *</Label>
            <Input
              value={props.newClient.name}
              onChange={(e) => props.setNewClient({ ...props.newClient, name: e.target.value })}
              placeholder="Ej: SEAL"
            />
          </div>
          <div>
            <Label className="text-xs">Código * (slug para prefijo ADV)</Label>
            <Input
              value={props.newClient.code}
              onChange={(e) => props.setNewClient({ ...props.newClient, code: e.target.value.toUpperCase() })}
              placeholder="SEAL"
              pattern="[A-Za-z0-9_-]+"
            />
            <p className="text-[10px] text-ink-400 mt-1">Los avisos se numerarán {props.newClient.code || "CODE"}-ADV-001, 002...</p>
          </div>
          <div>
            <Label className="text-xs">RUC</Label>
            <Input
              value={props.newClient.ruc}
              onChange={(e) => props.setNewClient({ ...props.newClient, ruc: e.target.value })}
              maxLength={11}
            />
          </div>
          <div>
            <Label className="text-xs">Contacto</Label>
            <Input
              value={props.newClient.contact_name}
              onChange={(e) => props.setNewClient({ ...props.newClient, contact_name: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Email contacto</Label>
            <Input
              type="email"
              value={props.newClient.contact_email}
              onChange={(e) => props.setNewClient({ ...props.newClient, contact_email: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Teléfono</Label>
            <Input
              value={props.newClient.phone}
              onChange={(e) => props.setNewClient({ ...props.newClient, phone: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Industria</Label>
            <Input
              value={props.newClient.industry}
              onChange={(e) => props.setNewClient({ ...props.newClient, industry: e.target.value })}
              placeholder="Energía eléctrica"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================
// Step 2 — Campaña
// =========================================================

function CampaignStep(props: { campaign: CampaignData; setCampaign: (c: CampaignData) => void }) {
  const c = props.campaign;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="md:col-span-2">
        <Label className="text-xs">Nombre de la campaña *</Label>
        <Input
          value={c.name}
          onChange={(e) => props.setCampaign({ ...c, name: e.target.value })}
          placeholder="SEAL — Avisos de corte 2026 Q3"
        />
      </div>
      <div>
        <Label className="text-xs">Código (opcional)</Label>
        <Input
          value={c.code}
          onChange={(e) => props.setCampaign({ ...c, code: e.target.value.toUpperCase() })}
          placeholder="CAMP-2026-Q3"
        />
      </div>
      <div>
        <Label className="text-xs">Presupuesto (PEN)</Label>
        <Input
          type="number" min="0" step="0.01"
          value={c.budget_amount}
          onChange={(e) => props.setCampaign({ ...c, budget_amount: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-xs">Inicio del período</Label>
        <DatePicker value={c.period_start} onChange={(e) => props.setCampaign({ ...c, period_start: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs">Fin del período</Label>
        <DatePicker value={c.period_end} onChange={(e) => props.setCampaign({ ...c, period_end: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">Descripción</Label>
        <Textarea
          value={c.description}
          onChange={(e) => props.setCampaign({ ...c, description: e.target.value })}
          rows={3}
        />
      </div>
    </div>
  );
}

// =========================================================
// Step 3 — Avisos
// =========================================================

function AdsStep(props: {
  ads: AdDraft[];
  addAd: () => void;
  updateAd: (idx: number, patch: Partial<AdDraft>) => void;
  removeAd: (idx: number) => void;
}) {
  return (
    <div className="space-y-3">
      {props.ads.map((ad, idx) => (
        <Card key={ad._key} className="border-ink-200">
          <CardContent className="py-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-mono text-ink-500">Aviso #{idx + 1}</div>
              <Button size="sm" variant="ghost" onClick={() => props.removeAd(idx)}>
                <Trash2 size={14} className="text-bad" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Título *</Label>
                <Input
                  value={ad.title}
                  onChange={(e) => props.updateAd(idx, { title: e.target.value })}
                  placeholder="Corte programado zona Arequipa Metropolitana"
                />
              </div>
              <div>
                <Label className="text-xs">Tipo *</Label>
                <Select value={ad.ad_type} onValueChange={(v) => props.updateAd(idx, { ad_type: v as AdType })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(AD_TYPE_LABELS).map(([k, lbl]) => (
                      <SelectItem key={k} value={k}>{lbl}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Resumen / contenido</Label>
                <Textarea
                  value={ad.summary}
                  onChange={(e) => props.updateAd(idx, { summary: e.target.value })}
                  rows={2}
                  placeholder="Texto del aviso a publicar..."
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      <Button variant="outline" onClick={props.addAd}>
        <Plus size={14} /> Añadir aviso
      </Button>
      {!props.ads.length && (
        <p className="text-xs text-ink-500 text-center py-4">
          Paso opcional. Puedes crear la campaña ahora y agregar los avisos luego
          en la sección <strong>Avisos</strong>.
        </p>
      )}
    </div>
  );
}

// =========================================================
// Step 4 — Placements
// =========================================================

function PlacementsStep(props: {
  ads: AdDraft[];
  placements: PlacementDraft[];
  addPlacement: (adIndex: number) => void;
  updatePlacement: (idx: number, patch: Partial<PlacementDraft>) => void;
  removePlacement: (idx: number) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-500">
        Paso opcional. Por cada aviso, define los medios donde se publicará
        (1 aviso → N placements). También puedes agregarlos luego desde el detalle del aviso.
      </p>
      {props.ads.map((ad, adIdx) => {
        const placementsForAd = props.placements
          .map((p, i) => ({ p, i }))
          .filter(({ p }) => p.ad_index === adIdx);
        return (
          <Card key={ad._key} className="border-ink-200">
            <CardHeader>
              <CardTitle className="text-sm">
                Aviso #{adIdx + 1}: {ad.title || "(sin título)"}
                <Badge variant="outline" className="ml-2">{ad.ad_type}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {placementsForAd.map(({ p, i }) => (
                <PlacementEditor
                  key={p._key}
                  placement={p}
                  onChange={(patch) => props.updatePlacement(i, patch)}
                  onRemove={() => props.removePlacement(i)}
                />
              ))}
              <Button size="sm" variant="outline" onClick={() => props.addPlacement(adIdx)}>
                <Plus size={14} /> Añadir medio
              </Button>
            </CardContent>
          </Card>
        );
      })}
      {!props.placements.length && (
        <p className="text-xs text-ink-500 text-center py-4">
          Paso opcional. Agrega placements ahora o luego desde el detalle del aviso.
        </p>
      )}
    </div>
  );
}

function PlacementEditor(props: {
  placement: PlacementDraft;
  onChange: (patch: Partial<PlacementDraft>) => void;
  onRemove: () => void;
}) {
  const p = props.placement;
  return (
    <div className="border border-ink-200 rounded-lg p-3 space-y-3 bg-ink-50/40">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-ink-700">Placement</div>
        <Button size="sm" variant="ghost" onClick={props.onRemove}>
          <Trash2 size={12} className="text-bad" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <OutletPicker
          value={p.outlet_id}
          onChange={(outlet_id, accepts) => props.onChange({ outlet_id, outlet_accepts: accepts })}
          label="Medio *"
        />
        <div>
          <Label className="text-xs">Cobertura *</Label>
          <Select value={p.coverage} onValueChange={(v) => props.onChange({
            coverage: v as Coverage,
            geo: { region_id: null, province_id: null, district_id: null },
          })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nacional">Nacional</SelectItem>
              <SelectItem value="regional">Regional</SelectItem>
              <SelectItem value="local">Local</SelectItem>
              <SelectItem value="zona">Zona</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <PeruGeoPicker
        coverage={p.coverage}
        value={p.geo}
        onChange={(geo) => props.onChange({ geo })}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Fecha solicitud</Label>
          <DatePicker value={p.requested_at} onChange={(e) => props.onChange({ requested_at: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Fecha programación</Label>
          <DatePicker value={p.scheduled_at} onChange={(e) => props.onChange({ scheduled_at: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Fecha finalización</Label>
          <DatePicker value={p.end_at} onChange={(e) => props.onChange({ end_at: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Duración (días)</Label>
          <Input type="number" min="0" value={p.duration_days}
            onChange={(e) => props.onChange({ duration_days: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Repeticiones</Label>
          <Input type="number" min="1" value={p.repetitions}
            onChange={(e) => props.onChange({ repetitions: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Spot (s)</Label>
          <Input type="number" min="0" value={p.spot_duration_seconds}
            onChange={(e) => props.onChange({ spot_duration_seconds: e.target.value })}
            placeholder="30, 60, 90" />
        </div>
        <div>
          <Label className="text-xs">Palabras</Label>
          <Input type="number" min="0" value={p.word_count}
            onChange={(e) => props.onChange({ word_count: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Formato</Label>
          <Select value={p.content_format} onValueChange={(v) => props.onChange({ content_format: v as ContentFormat })}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1/8">1/8 página</SelectItem>
              <SelectItem value="1/4">1/4 página</SelectItem>
              <SelectItem value="1/2">1/2 página</SelectItem>
              <SelectItem value="full_page">Página completa</SelectItem>
              <SelectItem value="30s">Spot 30s</SelectItem>
              <SelectItem value="60s">Spot 60s</SelectItem>
              <SelectItem value="90s">Spot 90s</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Costo (PEN) — vacío = auto</Label>
          <Input type="number" min="0" step="0.01" value={p.cost_amount}
            onChange={(e) => props.onChange({ cost_amount: e.target.value })}
            placeholder="Auto-calc desde tarifario" />
        </div>
        <div>
          <Label className="text-xs">Precio (PEN) — visible al cliente</Label>
          <Input type="number" min="0" step="0.01" value={p.price_amount}
            onChange={(e) => props.onChange({ price_amount: e.target.value })}
            placeholder="Vacío = sin monto para el cliente" />
        </div>
      </div>

      {/* Preview de costo si outlet tiene tarifario */}
      <PriceEstimatePreview
        outletId={p.outlet_id}
        wordCount={p.word_count ? Number(p.word_count) : null}
        spotDurationSeconds={p.spot_duration_seconds ? Number(p.spot_duration_seconds) : null}
        repetitions={Number(p.repetitions) || 1}
      />
    </div>
  );
}

// =========================================================
// Step 5 — Confirm
// =========================================================

function ConfirmStep(props: {
  choice: ClientChoice;
  clientName: string;
  campaign: CampaignData;
  ads: AdDraft[];
  placements: PlacementDraft[];
  submitting: boolean;
  progress: string;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-500">
        Revisa el resumen y confirma para crear todo en cascada.
      </p>

      <Card className="bg-ink-50 dark:bg-ink-800/50 border-ink-200">
        <CardContent className="py-4 space-y-3 text-sm">
          <Row label="Cliente" value={`${props.clientName} ${props.choice === "new" ? "(nuevo)" : "(existente)"}`} />
          <Row label="Campaña" value={props.campaign.name} />
          {props.campaign.period_start && props.campaign.period_end && (
            <Row label="Período" value={`${props.campaign.period_start} → ${props.campaign.period_end}`} />
          )}
          {props.campaign.budget_amount && (
            <Row label="Presupuesto" value={`PEN ${Number(props.campaign.budget_amount).toLocaleString("es-PE")}`} />
          )}
          <Row label="Avisos a crear" value={`${props.ads.length}`} />
          <Row label="Placements totales" value={`${props.placements.length}`} />
        </CardContent>
      </Card>

      <div className="space-y-2">
        {props.ads.map((ad, idx) => {
          const adPlacements = props.placements.filter(p => p.ad_index === idx);
          return (
            <div key={ad._key} className="border border-ink-200 rounded p-3 text-xs">
              <div className="font-semibold">
                <Badge variant="outline" className="mr-2">{ad.ad_type}</Badge>
                {ad.title}
              </div>
              <div className="text-ink-500 mt-1">
                {adPlacements.length} placements
              </div>
            </div>
          );
        })}
      </div>

      {props.submitting && (
        <div className="flex items-center gap-2 text-sm text-brand-700 bg-brand-50 p-3 rounded">
          <Loader2 size={14} className="animate-spin" />
          {props.progress || "Procesando..."}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-xs text-ink-500 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-right text-ink-900 dark:text-white font-medium">{value}</div>
    </div>
  );
}
